import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';

import {
    withStyles, Typography, Button, LinearProgress,
} from '@material-ui/core';

import { hasValue } from '../../youwell-common/objectUtils';
import { HtmlContent, TaskContentDisplay } from '../../youwell-common/components';
import { activityLabels, patientProgramElementGroups, patientProgramAppElementSettings } from '../../common/constants';
import { InfoMessage, ModuleProgress, TrackedLink, LanguageToggle } from '../../common/components';
import { getTaskFromElement, hasElementSetting } from '../../common/taskUtils';
import { PROGRAM_CURRENTPROGRAM_RECEIVED } from '../../app/store/actions';
import UnfinishedTasksBox from './components/UnfinishedTasksBox';
import ModuleBoxFlat from './components/ModuleBoxFlat';
import CurrentModuleBox from './components/CurrentModuleBox';
import ScheduledTasksBox from './components/ScheduledTasksBox';
import CalendarBox from './components/CalendarBox';

import { dashboardItems, dashboardItemsCustom } from '../../app/appSetup';
import AppointmentBox from './components/AppointmentBox';
import urls from '../../app/urls';
import ModuleBoxWithOverlay from './components/ModuleBoxWithOverlay';

const styles = theme => ({
    root: {
        padding: theme.spacing(6, 12),

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3, 4.5),
        },

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
        },
    },
    welcome: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    welcomeContent: {
        flex: '1 1 auto',
    },
    welcomeTitle: {
        fontWeight: 500,
        outline: 'none',
    },
    logo: {
        width: 150,

        [theme.breakpoints.down('xs')]: {
            width: 50,
        },
    },
    programDescription: {
        marginTop: theme.spacing(1),
        clear: 'both',
    },
    waitMessage: {
        marginBottom: theme.spacing(8),
    },
    content: {
        marginTop: theme.spacing(8),
    },
    contentBox: {
        marginTop: theme.spacing(8),
        background: theme.palette.background.default,
        boxShadow: theme.youwell.shadows.default,
        borderRadius: theme.youwell.shapes.borderRadius,
    },
    active: {
        // border: theme.youwell.borders.primary,
    },
    moduleTitle: {
        borderLeft: theme.youwell.borders.secondary,
        borderLeftWidth: 4,
        paddingLeft: theme.spacing(2),
        marginBottom: theme.spacing(2),
        outline: 'none',
    },
    modules: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    module: {
        flexBasis: `calc(-${theme.spacing(4)}px + 33.3333%)`, // IE calc. Renders wrong for `calc((100% - ${theme.spacing(12)}px) / 3)`
        marginBottom: theme.spacing(4),

        '&:nth-child(3n+2)': {
            [theme.breakpoints.up('md')]: {
                marginLeft: theme.spacing(6),
                marginRight: theme.spacing(6),
            },
        },

        [theme.breakpoints.down('sm')]: {
            flexBasis: `calc(-${theme.spacing(2)}px + 50%)`, // IE calc. Renders wrong for `calc((100% - ${theme.spacing(4)}px) / 2)`
        },

        '&:nth-child(2n + 1)': {
            [theme.breakpoints.down('sm')]: {
                marginRight: theme.spacing(4),
            },
            [theme.breakpoints.down('xs')]: {
                marginRight: 0,
            },
        },

        [theme.breakpoints.down('xs')]: {
            flexBasis: '100%',
            justifyContent: 'center',
        },
    },
    exitButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(8),
    },
    exitButton: {
        minWidth: 300,
        padding: theme.spacing(1),
    },
    infoContent: {
        margin: theme.spacing(2),
    },
    progressContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 2,
        alignItems: 'center',
        justifyContent: 'flex-start',

        '@media print': {
            display: 'none',
        },
    },
    progressBar: {
        height: 12,
        maxWidth: 200,
        minWidth: 120,
        flexGrow: 1,
    },
    progressBarRounded: {
        background: theme.palette.primary.main,
        borderRadius: theme.youwell.shapes.borderRadius,
        border: '2px solid',
    },
    progressBarFill: {
        background: theme.youwell.colors.highlight,
        borderRadius: theme.youwell.shapes.borderRadius,
    },
    progressText: {
        marginLeft: theme.spacing(1),
    },
});

class Dashboard extends React.Component {
    componentDidMount() {
        if (this.props.prevPathname?.startsWith('/inside/program')) {
            if (this.modulesRef) this.modulesRef.focus();
        }
    }

    componentDidUpdate(prevProps) {
        // Perform update when hasLoaded changes
        if (prevProps.hasLoaded !== this.props.hasLoaded) {
            if (this.titleRef) this.titleRef.focus();
        }
    }

    getNextElementIndex = () => this.props.programDetails.groupedElements[patientProgramElementGroups.main]?.findIndex(d => !d.completed && !d.disabled)

    getIntroElement = completed => this.props.programDetails?.appElements?.find(d => hasElementSetting(d, patientProgramAppElementSettings.intro, true) && !d.disabled && (completed ? d.completed : !d.completed));

    getDashboardInfoElement = () => this.props.programDetails?.appElements?.find(d => hasElementSetting(d, patientProgramAppElementSettings.dashboardInfo) && !d.disabled);

    renderItem = (item) => {
        const {
            classes, translate, programDetails, profileData, clientConfig, appConfig,
        } = this.props;

        const mainElements = programDetails.groupedElements[patientProgramElementGroups.main] || [];
        const progress = programDetails.completedModules / programDetails.totalModules;
        const showLanguageToggle = !appConfig.language;

        switch (item) {
        case dashboardItems.welcome: {
            const firstVisit = true; // check to be implemented..
            return (
                <div className={classes.welcome}>
                    <div className={classes.welcomeContent}>
                        {profileData?.name ? (
                            <Typography variant="h5" className={classes.welcomeTitle} ref={(thisRef) => { this.titleRef = thisRef; }} tabIndex="-1">
                                {translate(firstVisit ? 'dashboard.welcomeUser' : 'dashboard.welcomeBackUser', { name: profileData.name || '' })}
                            </Typography>
                        ) : (
                            <Typography variant="h6" className={classes.welcomeTitle} ref={(thisRef) => { this.titleRef = thisRef; }} tabIndex="-1">
                                {translate('dashboard.welcome')}
                            </Typography>
                        )}
                    </div>
                    <AppointmentBox
                        elements={programDetails.groupedElements[patientProgramElementGroups.appointment]}
                    />
                    {showLanguageToggle && <LanguageToggle />}
                </div>
            );
        }
        case dashboardItems.programDescription: {
            return (
                <>
                    {clientConfig.appSetup.features.progress && (
                        <div className={classes.progressContainer}>
                            {hasValue(progress) && programDetails.isActiveProgram && (
                                <>
                                    <LinearProgress variant="determinate" value={progress * 100} className={classes.progressBar} classes={{ root: classes.progressBarRounded, bar: classes.progressBarFill }} />
                                    <Typography variant="caption" className={classes.progressText} align="center" display="block">
                                        {translate('common.progressCompleted', { progress: Math.round(progress * 100) })}
                                    </Typography>
                                </>
                            )}
                        </div>
                    )}
                    <HtmlContent html={programDetails.program.description} className={classes.programDescription} />
                </>
            );
        }
        case dashboardItems.questionnaires: {
            if (!programDetails.isActiveProgram) return null;
            const availableScheduledTasks = programDetails.scheduledElements
                .filter(d => !d.completed && !d.disabled);

            return (
                <ScheduledTasksBox
                    className={classnames(classes.contentBox, classes.active)}
                    elements={availableScheduledTasks}
                    programId={programDetails.id}
                />
            );
        }
        case dashboardItems.intro: {
            if (!programDetails.isActiveProgram) return null;
            const introElement = this.getIntroElement();

            return introElement && (
                <CurrentModuleBox
                    className={classnames(classes.contentBox, classes.active)}
                    programId={programDetails.id}
                    trackLabel={activityLabels.dashboard.introModule}
                    element={introElement}
                    index={introElement.indexInGroup}
                    hideProgress
                    title={translate('dashboard.introduction')}
                />
            );
        }
        case dashboardItems.modules: {
            const nextElementIndex = this.getNextElementIndex();

            return mainElements?.length > 0 && (
                <div className={classes.content}>
                    <Typography variant="h6" className={classes.moduleTitle} ref={(thisRef) => { this.modulesRef = thisRef; }} tabIndex="-1">
                        {translate('dashboard.modules')}
                    </Typography>

                    <div className={classes.modules}>
                        {mainElements.map((element, i) => (
                            clientConfig.appSetup.features.useFlatModuleBoxes
                                ? (
                                    <ModuleBoxFlat
                                        key={element.id}
                                        programId={programDetails.id}
                                        element={element}
                                        index={i}
                                        isCurrent={i === nextElementIndex}
                                        className={classes.module}
                                        descriptionHeight={clientConfig.customs?.moduleBoxDescriptionHeight}
                                    />
                                ) : (
                                    <ModuleBoxWithOverlay
                                        key={element.id}
                                        programId={programDetails.id}
                                        element={element}
                                        index={i}
                                        // isCurrent={i === nextElementIndex}
                                        className={classes.module}
                                        to={`${urls.inside.program}/${element.elementGroup}/${i}/${urls.inside.parts.resumeLatest}`}
                                    />
                                )
                        ))}
                    </div>
                </div>
            );
        }
        case dashboardItemsCustom.unfinished: {
            if (!programDetails.isActiveProgram) return null;
            const unfinishedElements = mainElements.reduce((prev, curr) => {
                if (!curr || !curr.module || !curr.module.tasks || curr.totalTasksVisited < 1) {
                    return prev;
                }

                const current = {
                    id: curr.id,
                    indexInGroup: curr.indexInGroup,
                    moduleName: curr.name,
                    tasks: curr.module.tasks.map((t, taskIndex) => ({
                        id: t.id, name: t.name, taskIndex, ok: t.completed || !t.visited,
                    })).filter(d => !d.ok),
                };

                if (current.tasks.length === 0) {
                    return prev;
                }

                return prev.length > 0 ? [...prev, current] : [current];
            }, []);

            return (
                <UnfinishedTasksBox
                    className={classnames(classes.contentBox, classes.active)}
                    elements={unfinishedElements}
                    programId={programDetails.id}
                />
            );
        }
        case dashboardItemsCustom.progress: {
            if (!programDetails.isActiveProgram) return null;
            const nextElementIndex = this.getNextElementIndex();
            const currentModule = nextElementIndex > -1 && nextElementIndex < mainElements.length ? mainElements[nextElementIndex] : null;

            return (
                <CurrentModuleBox
                    className={classnames(classes.contentBox, classes.active)}
                    programId={programDetails.id}
                    trackLabel={activityLabels.dashboard.currentModule}
                    element={currentModule}
                    index={nextElementIndex}
                    title={translate('dashboard.currentProgressInApp')}
                />
            );
        }
        case dashboardItemsCustom.calendar: {
            return (
                <CalendarBox
                    className={classnames(classes.contentBox, classes.active)}
                />
            );
        }
        case dashboardItemsCustom.completedIntro: {
            const introElement = this.getIntroElement(true);

            return introElement && (
                <CurrentModuleBox
                    className={classnames(classes.contentBox)}
                    programId={programDetails.id}
                    trackLabel={activityLabels.dashboard.calendar}
                    element={introElement}
                    index={introElement.indexInGroup}
                    hideProgress
                    title={translate('dashboard.introduction')}
                />
            );
        }
        case dashboardItemsCustom.waitingForScheduled: {
            const hasAvailableScheduledTasks = programDetails.scheduledElements
                .filter(d => !d.completed && !d.disabled).length > 0;

            return hasAvailableScheduledTasks ? null : (
                <InfoMessage messageKey="dashboard.waitingForScheduled" />
            );
        }
        case dashboardItemsCustom.exit: {
            return (
                <div className={classes.exitButtonContainer}>
                    <Button
                        variant="contained"
                        color="primary"
                        component={TrackedLink}
                        to={urls.logout}
                        className={classes.exitButton}
                        label={activityLabels.dashboard.exit}
                    >
                        {translate('dashboard.exit')}
                    </Button>
                </div>
            );
        }
        case dashboardItemsCustom.info: {
            const infoElement = this.getDashboardInfoElement();
            const task = getTaskFromElement(infoElement);

            return task && (
                <div className={classes.infoContent}>
                    <TaskContentDisplay
                        task={task}
                    />
                </div>
            );
        }
        default:
            return null;
        }
    }

    render() {
        const {
            classes, programDetails, hasLoaded, clientConfig, appConfig,
        } = this.props;

        if (!clientConfig) {
            return null;
        }

        const showLanguageToggle = !appConfig.language;

        if (!programDetails) {
            if (hasLoaded) {
                return (
                    <div className={classes.root}>
                        <InfoMessage messageKey="dashboard.noProgramAssigned" />
                        {showLanguageToggle && <LanguageToggle />}
                    </div>
                );
            }
            return null;
        }

        return (
            <>
                {clientConfig.appSetup.features.moduleProgress && <ModuleProgress className={classes.moduleProgress} />}

                <div className={classes.root}>
                    {clientConfig.appSetup.dashboard.map(item => (
                        <React.Fragment key={item}>
                            {this.renderItem(item)}
                        </React.Fragment>
                    ))}
                </div>
            </>
        );
    }
}

Dashboard.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    clientConfig: PropTypes.object,
    programDetails: PropTypes.object,
    hasLoaded: PropTypes.bool,
    prevPathname: PropTypes.string,
    profileData: PropTypes.object,
    appConfig: PropTypes.object,
};

Dashboard.defaultProps = {
    clientConfig: null,
    programDetails: null,
    hasLoaded: false,
    prevPathname: null,
    profileData: null,
    appConfig: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    prevPathname: state.pageSettings.prevPathname,
    profileData: state.auth.profileData,
    clientConfig: state.auth.clientConfig,
    appConfig: state.pageSettings.appConfig,
    programDetails: state.programStore.currentProgram,
    hasLoaded: Boolean(state.programStore.requests[PROGRAM_CURRENTPROGRAM_RECEIVED]
        && state.programStore.requests[PROGRAM_CURRENTPROGRAM_RECEIVED].completed),
});

export default connect(mapStateToProps)(withStyles(styles)(Dashboard));
